import {filter, find, map, size} from 'lodash';
import {useContext, useMemo, useCallback, useState, useEffect} from 'react';

import {CLIENT_PROCESS_ASYNC_REQUESTS, SystemEndSessionCode} from 'constants/session';
import {ClientContext} from 'context';
import {usePrevious} from 'hooks';
import {
    AgentParticipant,
    AsyncRequestStatusName,
    AudioVideoView,
    GuestParticipant,
    ScreenSharingActivityStatus,
    SessionActivityName,
    SessionStatus,
    WebRTCSpeechActivityData
} from 'interfaces';
import {ClientAppearanceFeatures, ClientSessionResponsePayload} from 'pages/Client/types';
import {ParticipantRole} from 'types';
import {getScreenSharingPresenterId, getWebRTCRoomName, sessionUtils} from 'utils';

export function useClientSession(data?: {payload: ClientSessionResponsePayload}) {
    const sessionContext = useContext(ClientContext);
    const dataSource = useMemo(() => data?.payload || sessionContext, [data, sessionContext]);
    const token = useMemo(() => dataSource?.token, [dataSource?.token]);
    const {session, settings, assignmentState} = useMemo(() => dataSource, [dataSource]);
    const agentsUnavailable = useMemo(() => !!assignmentState && !assignmentState?.agentsAvailable, [assignmentState]);
    const isAppointmentRequest = useMemo(() => session?.result?.information === SystemEndSessionCode.ClientAppointmentRequest, [session]);
    const isAfterHours = useMemo(() => session?.result?.information === SystemEndSessionCode.AfterHours, [session]);
    const [latestToken, setLatestToken] = useState(token);

    const sessionData = useMemo(() => ({
        session,
        sessionId: session?.id,
        localParticipant: session?.localParticipant,
        isWebviewVideoOnly: session?.localParticipant?.data?.appearance?.features === ClientAppearanceFeatures.Video,
        downloadFileDisabled: session?.localParticipant?.data?.appearance?.webview,
        inWidget: session?.localParticipant?.data?.inWidget,
        sessionVersion: session?.version,
        participantId: session?.localParticipant?.id,
        agent: find(session?.participants, {role: 'agent'}) as AgentParticipant,
        startDate: session?.startDate,
        isNew: session?.status === SessionStatus.New,
        isInSession: session?.status === SessionStatus.InSession,
        isPreCall: session?.status === SessionStatus.PreCall,
        sessionStatus: session?.status,
        isEnded: !!session?.endDate,
        scheduleStartDate: session?.schedule?.startDate,
        statusDate: session?.statusDate,
        participantIds: map(session?.participants, (p) => p.id),
        asyncRequestToHandle: find(session?.localParticipant?.asyncRequests, (asyncRequest) =>
            CLIENT_PROCESS_ASYNC_REQUESTS.includes(asyncRequest.type) &&
            asyncRequest.status?.name === AsyncRequestStatusName.InProgress),
        isClient: session?.localParticipant?.role === ParticipantRole.Client,
        isGuest: (session?.localParticipant as any as GuestParticipant)?.role === ParticipantRole.Guest,
    }), [session]);

    const prevStatus = usePrevious(sessionData.sessionStatus);

    const sessionParams = useMemo(() => {
        const {sessionId, participantId, sessionVersion} = sessionData;
        return {sessionId, participantId, fv: sessionVersion};
    }, [sessionData]);

    const getActivityByName = useCallback((name: SessionActivityName) => find(session?.activities, {name}), [session]);

    const settingsData = useMemo(() => {
        const feedbackRatingEnabled = !!settings?.feedback?.rating?.enabled;
        const feedbackCommentEnabled = !!settings?.feedback?.comment?.enabled;

        const audioVideoView = size(session?.participants) > 1 ?
            AudioVideoView.Conference : settings?.activities?.audioVideo?.defaultView;

        return {
            language: settings?.common?.language,
            timeZone: settings?.common?.timeZone,
            dateFormat: settings?.common?.dateFormat,
            timeFormat: settings?.common?.timeFormat,
            workingHours: settings?.workingHours,
            notifyStatuses: settings?.notifyStatuses,
            image: settings?.image?.path || undefined,
            videoPinEnabled: settings?.videoPin?.enabled,
            videoPinnedParticipant: settings?.videoPin?.participant,
            onHoldImage: settings?.status?.onHold?.image,
            requestAppointmentEnabled: !!settings?.requestAppointmentEnabled,
            canSendFeedback: !!session?.answered && (feedbackRatingEnabled || feedbackCommentEnabled),
            subtitlesEnabled: !!settings?.subtitles?.enabled,
            avatarHost: settings?.activities?.avatar?.config?.host,
            avatarUrl: settings?.activities?.avatar?.config?.url,
            avatarZoom: settings?.activities?.avatar?.config?.avatarZoom,
            avatarBackgroundZoom: settings?.activities?.avatar?.config?.backgroundZoom,
            avatarBackgroundImageUrl: settings?.activities?.avatar?.config?.backgroundImageUrl,
            intermediateMessages: settings?.intermediateMessages,
            feedbackRatingEnabled,
            feedbackRatingView: settings?.feedback?.rating?.view,
            feedbackRatingDirection: settings?.feedback?.rating?.direction,
            feedbackRatingLabels: settings?.feedback?.rating?.labels || [],
            feedbackCommentEnabled,
            fullScreenVideoDefault: settings?.activities?.audioVideo?.browser?.fullScreen,
            canSwitchCamera: !!settings?.canSwitchCamera,
            controlsAutoHideEnabled: settings?.controls?.autoHide?.enabled,
            controlsAutoHideTimeout: settings?.controls?.autoHide?.timeout,
            controlsInitialVisible: settings?.controls?.initialVisible,
            isPictureInPictureEnabled: settings?.pictureInPicture?.enabled,
            ticker: settings?.ticker,
            idleReminder: settings?.idleReminder,
            audioVideoView,
            audioVideoViewConference: audioVideoView === AudioVideoView.Conference,
        };
    }, [session, settings]);

    const activitiesData = useMemo(() => {
        const audioVideoActivity = getActivityByName(SessionActivityName.AudioVideo);
        const audioActivity = getActivityByName(SessionActivityName.Audio);
        const sipActivity = getActivityByName(SessionActivityName.SIP);
        const avatarActivity = getActivityByName(SessionActivityName.Avatar);
        const textChatActivity = getActivityByName(SessionActivityName.TextChat);
        const screenSharingActivity = getActivityByName(SessionActivityName.ScreenSharing);
        const coBrowsingActivity = getActivityByName(SessionActivityName.CoBrowsing);
        const screenSharingActivityStatus: ScreenSharingActivityStatus = screenSharingActivity?.data?.status;
        const isScreenSharingPresenter = getScreenSharingPresenterId(session) === sessionData.participantId;

        return {
            audioVideoActivity,
            audioActivity,
            sipActivity,
            avatarActivity,
            textChatActivity,
            screenSharingActivity,
            coBrowsingActivity,
            webRTCActivities: filter([audioVideoActivity, audioActivity, screenSharingActivity]),
            screenSharingActivityStatus,
            videoMinimized: sessionUtils.getVideoMinimized(session) || !!sessionData?.asyncRequestToHandle,
            webRTCRoomName: getWebRTCRoomName(session),
            activitiesCount: session?.activities.length,
            isScreenSharingPresenter,
            transcriptionLanguage: (audioActivity?.data || audioVideoActivity?.data)
                ?.translationSettings?.[session?.localParticipant?.id]?.language,
            audioVideoAsColumn: !!audioVideoActivity && (
                (screenSharingActivityStatus === ScreenSharingActivityStatus.Accepted && !isScreenSharingPresenter)
                            || !!coBrowsingActivity
                            || !!sessionData?.asyncRequestToHandle
            ) && settingsData?.audioVideoViewConference
        };
    }, [session, sessionData, settingsData, getActivityByName]);

    const audioStreamEnabled = useMemo(() => settings?.audioStream?.enabled && !!(
        (activitiesData.audioActivity?.data ||
            activitiesData.audioVideoActivity?.data ||
            activitiesData.avatarActivity?.data ||
            // Voice input in the text chat: the client may speak instead of typing.
            activitiesData.textChatActivity?.data
        ) as WebRTCSpeechActivityData
    )?.streamAudio?.enabled, [
        activitiesData.audioActivity?.data,
        activitiesData.audioVideoActivity?.data,
        activitiesData.avatarActivity?.data,
        activitiesData.textChatActivity?.data,
        settings?.audioStream?.enabled
    ]);

    const isCurrentStatus = useCallback((status: SessionStatus) => session?.status === status, [session]);
    const isPreviousStatus = useCallback((status: SessionStatus) => prevStatus === status, [prevStatus]);

    useEffect(() => {
        if (token && token !== latestToken) {
            setLatestToken(token);
        }
    }, [token, latestToken]);

    return {
        token, latestToken, agentsUnavailable, isAfterHours, ...sessionData, sessionParams, ...activitiesData, ...settingsData,
        isCurrentStatus, isPreviousStatus, isAppointmentRequest, audioStreamEnabled
    };
}